import { createContext, useReducer } from "react";
import getAssetStyleInfo from "./AssetStyleInfo";
import { toolReducer } from "../reducers/toolReducer";
import * as tab from "../constants/tabs";
import { ReactNode } from "react";
import { RingfencedElement } from "../model/viewModel/ringfencedElement";
import { SitePlan } from "../components/site-plan/UseSitePlan";
import { Asset } from "../model/viewModel/asset";
import { Toolbar } from "../model/internal/toolbar";
import { AssetStyleInfo } from "../model/internal/assetInfo";
import { getSelectedToolDefault } from "../utils/toolbarFunctions";

const toolbar: Toolbar = [
  {
    name: "Select",
    items: [
      getAssetStyleInfo("select"),
      getAssetStyleInfo("select-group"),
      getAssetStyleInfo("re-shape"),
      getAssetStyleInfo("select-results"),
    ],
  },
  {
    name: "Delete",
    items: [getAssetStyleInfo("delete"), getAssetStyleInfo("delete-group")],
  },
  {
    name: "Substation",
    items: [getAssetStyleInfo("substation-gmt"), getAssetStyleInfo("substation-pmt")],
  },
  {
    name: "Cable",
    items: [
      getAssetStyleInfo("underground-cable"),
      getAssetStyleInfo("overhead-line"),
      getAssetStyleInfo("node"),
      getAssetStyleInfo("editCable"),
    ],
  },
  {
    name: "Customer",
    items: [
      getAssetStyleInfo("domestic"),
      getAssetStyleInfo("small-commercial"),
      getAssetStyleInfo("large-commercial"),
      getAssetStyleInfo("industrial"),
      getAssetStyleInfo("generator"),
      getAssetStyleInfo("welder"),
      getAssetStyleInfo("motor"),
      getAssetStyleInfo("evChargePoint"),
      getAssetStyleInfo("heatPump"),
      getAssetStyleInfo("point-of-connection"),
    ],
  },
  {
    name: "Annotation",
    items: [
      getAssetStyleInfo("sticky-note"),
      getAssetStyleInfo("rectangle"),
      getAssetStyleInfo("circle"),
      getAssetStyleInfo("arrow"),
    ],
  },
  {
    name: "Pipette",
    items: [],
    topLevelItem: getAssetStyleInfo("pipette"),
    requiredFeature: "CloneToolEnabled",
  },
];

const modes = [
  {
    name: "Select",
    items: [
      {
        name: "Select",
        class: "select",
      },
      {
        name: "Select Group",
        class: "select",
      },
    ],
  },
  {
    name: "Delete",
    items: [
      {
        name: "Delete",
        class: "delete",
      },
      {
        name: "Delete Group",
        class: "delete",
      },
    ],
  },
];

// Please update this with additional types as and when their respective files are converted to TypeScript and the Types become apparent
export type ToolState = {
  mode: string;
  modes: { name: string; items: { name: string; class: string }[] }[];
  selectedMode: number[];
  toolbar: Toolbar;
  selectedTool: number[];
  tool: string;
  activeCategory: number;
  activeTool: string | AssetStyleInfo;
  selectedObj: Record<string, unknown>;
  sidebar: boolean;
  clickedAsset: Asset | unknown | null;
  draggingArrowVertex: boolean;
  snapTargetAssetId: string | unknown | null;
  snapExcludedAssetId: string | unknown | null;
  showResults: boolean;
  alert: Record<string, unknown>;
  warnings: Record<string, unknown>;
  drawMarker: boolean;
  drawCable: boolean;
  drawBoundary: boolean;
  currentLatLng: { lat: number; lng: number } | null;
  mountEvent: unknown | null;
  assessmentRunning: boolean;
  assessmentFailed: boolean;
  costingFailed: boolean;
  lines: boolean;
  geometry: boolean;
  linesLoaded: boolean;
  geometryLoaded: boolean;
  activeTab: string;
  feedbackForm: boolean;
  isLoading: boolean;
  ringfenced: RingfencedElement[];
  ringfencedFiltered: RingfencedElement[];
  ringfencedTypes: any;
  disableBoundary: boolean;
  feederList: unknown | null;
  filteredFeeders: Record<string, unknown>;
  searchValue: string | null;
  errors: Record<string, unknown>;
  ringfenceUpdated: boolean;
  leafletMap: L.Map | null;
  showNearbyAssets: { disabled: boolean; substations: boolean };
  showMainsOnly: { disabled: boolean; mains: boolean };
  mainsOnly: boolean;
  electricOfficeOnly: boolean;
  navigateToLatLng: { lat: number; lng: number } | null;
  selectedAssetId: string | null;
  consumerCountChangedId: string;
  useAntData: boolean;
  originalSitePlan: SitePlan | null;
  sitePlan: SitePlan | null;
  sitePlanSelected: boolean;
  clickedFaultDetectionCable: unknown | null;
  matchedSearchIds: string[];
  keyDown: Record<string, unknown>;
  groupClick: number;
  skipAdmd: boolean;
  showFeederList: boolean;
  popupResultsAssetId: string | null;
  showCommittedLoadMessage: boolean;
  propertiesClipboard: { asset: Asset | null; lastPastedToId: string | null } | null;
  clickedLayerAsset: string | null;
};

export const initialState: ToolState = {
  mode: "select",
  modes: modes,
  selectedMode: modes.map((t) => 0),
  toolbar: toolbar,
  selectedTool: getSelectedToolDefault(),
  tool: "",
  activeCategory: 0,
  activeTool: "",
  selectedObj: {},
  sidebar: true,
  clickedAsset: null,
  draggingArrowVertex: false,
  snapTargetAssetId: null,
  snapExcludedAssetId: null,
  showResults: false,
  alert: {},
  warnings: {},
  drawMarker: false,
  drawCable: false,
  drawBoundary: false,
  currentLatLng: null,
  mountEvent: null,
  assessmentRunning: false,
  assessmentFailed: false,
  costingFailed: false,
  lines: false,
  geometry: false,
  linesLoaded: false,
  geometryLoaded: false,
  activeTab: tab.FILE,
  feedbackForm: false,
  isLoading: false,
  ringfenced: [],
  ringfencedFiltered: [],
  ringfencedTypes: [],
  disableBoundary: false,
  feederList: null,
  filteredFeeders: {},
  searchValue: null,
  errors: {},
  ringfenceUpdated: false,
  leafletMap: null,
  showNearbyAssets: {
    disabled: false,
    substations: true,
  },
  showMainsOnly: {
    disabled: false,
    mains: true,
  },
  mainsOnly: false,
  electricOfficeOnly: true,
  navigateToLatLng: null,
  selectedAssetId: null,
  consumerCountChangedId: "",
  useAntData: true,
  originalSitePlan: null,
  sitePlan: null,
  sitePlanSelected: false,
  clickedFaultDetectionCable: null,
  matchedSearchIds: [],
  keyDown: {},
  groupClick: 0,
  skipAdmd: false,
  showFeederList: false,
  popupResultsAssetId: null,
  showCommittedLoadMessage: false,
  propertiesClipboard: null,
  clickedLayerAsset: null,
};

export const ToolContext = createContext<{
  toolState: ToolState;
  setToolState: React.Dispatch<any>;
}>({
  toolState: initialState,
  setToolState: () => null,
});

const ToolContextProvider = ({ children }: { children: ReactNode }) => {
  const [toolState, setToolState] = useReducer(toolReducer, initialState);
  return (
    <ToolContext.Provider value={{ toolState, setToolState }}>{children}</ToolContext.Provider>
  );
};

export default ToolContextProvider;
